import { Button, Card, Heading, IconButton, Input, Text, toast } from "@overmap-ai/blocks"
import {
	OrganizationAccessLevel,
	type Stored,
	type Team,
	ABBREVIATION_MAX_LENGTH,
	ABBREVIATION_MIN_LENGTH,
	ERROR_MSG_UNEXPECTED,
} from "@overmap-ai/common"
import { APIError, selectTeamById } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { type FocusEventHandler, memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { TextConfirmationDialog } from "../../components/Dialogs/TextConfirmationDialog"
import { LuDynamicIcon } from "../../components/Icons/LuDynamicIcon"
import type { IconType } from "../../components/Icons/typings"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { IconColorPickerPopover } from "../../components/Popovers/IconColorPickerPopover"
import { RouteLinksCard } from "../../components/RouteLinksCard/RouteLinksCard"
import type { RouteLinksCardRoute } from "../../components/RouteLinksCard/typings"
import { useUserOrganizationAccess } from "../../hooks/users"
import { useBlurInput } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/teams/$teamId")

const routes: RouteLinksCardRoute[] = [
	{
		icon: modelIconMapping.user,
		label: "Members",
		description: "Manage team members.",
		from: "/$organizationId/settings/teams/$teamId",
		to: "/$organizationId/settings/teams/$teamId/members",
	},
]

export const TeamSettingsDetailView = memo(() => {
	const { teamId } = routeApi.useParams()

	const team = useAppSelector(selectTeamById(teamId))

	if (!team) throw new Error(`Expected team to be defined, but got ${team}`)

	const isOrganizationAdmin = useUserOrganizationAccess(OrganizationAccessLevel.ADMIN)
	const navigate = useNavigate()

	const handlePatchTeam = useCallback(
		(fields: Partial<Stored<Team>>) => {
			const [_, promise] = sdk.teams.update({ uuid: team.uuid, ...fields })

			promise.catch((error) => {
				toast.error({
					title: "Failed to update team",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[team.uuid],
	)

	const handleTeamNameInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const teamName = e.target.value
			if (!teamName) {
				toast.error({
					title: "Team name cant be blank",
					description: "Please enter a name for the team",
				})
				return
			}
			handlePatchTeam({ name: teamName })
		},
		[handlePatchTeam],
	)

	const handleDeleteTeam = useCallback(async () => {
		await navigate({
			from: "/$organizationId/settings/teams/$teamId",
			to: "/$organizationId/settings/teams",
		})

		sdk.teams.delete(team.uuid).catch((error) => {
			toast.error({
				title: "Failed to delete team",
				description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
			})
		})
	}, [navigate, team.uuid])

	const teamNameInputProps = useBlurInput(team.name, handleTeamNameInputBlur, true)

	const handleAbbrInputBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
		(e) => {
			const value = e.target.value
			handlePatchTeam({ abbr: value })
		},
		[handlePatchTeam],
	)

	const abbrInputProps = useBlurInput<HTMLInputElement>(team.abbr, handleAbbrInputBlur, true)

	const handleColorChange = useCallback(
		(color: string | null) => {
			if (!color) return
			handlePatchTeam({ color: color })
		},
		[handlePatchTeam],
	)

	const handleIconChange = useCallback(
		(icon: string | null) => {
			if (!icon) return
			handlePatchTeam({ icon })
		},
		[handlePatchTeam],
	)

	return (
		<ViewContainer maxWidth="2xl">
			<section className="flex h-max w-full flex-col gap-4">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{team.name}</Heading>
					<Text size="sm" accentColor="base">
						Manage team settings and members
					</Text>
				</div>

				<Card className="flex w-full flex-col gap-2" size="sm">
					<div className="flex w-full gap-2">
						<div className="flex w-full flex-col gap-1">
							<Text size="sm" accentColor="base">
								Icon & name
							</Text>
							<div className="flex w-full gap-2">
								<IconColorPickerPopover
									className="w-[340px]"
									color={team.color}
									onColorChange={handleColorChange}
									selectedIcon={team.icon}
									onIconChange={handleIconChange}
									size="sm"
								>
									<IconButton
										aria-label="icon"
										size="md"
										accentColor="base"
										variant="soft"
										disabled={!isOrganizationAdmin}
									>
										<LuDynamicIcon icon={team.icon as IconType} color={team.color} />
									</IconButton>
								</IconColorPickerPopover>
								<Input.Root variant="outline">
									<Input.Field
										disabled={!isOrganizationAdmin}
										placeholder="Enter a team name"
										{...teamNameInputProps}
									/>
								</Input.Root>
							</div>
						</div>
						<div className="flex w-full flex-col gap-1">
							<Text accentColor="base" size="sm">
								Abbreviation
							</Text>
							<Input.Root variant="outline">
								<Input.Field
									minLength={ABBREVIATION_MIN_LENGTH}
									maxLength={ABBREVIATION_MAX_LENGTH}
									placeholder="Enter an abbreviation"
									disabled={!isOrganizationAdmin}
									{...abbrInputProps}
								/>
							</Input.Root>
						</div>
					</div>
				</Card>

				<RouteLinksCard routes={routes} />

				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
					<TextConfirmationDialog
						confirmString="Delete"
						actionLabel="Delete"
						description={`This will irrevocably delete the team ${team.name}. This action cannot be undone.`}
						confirmMessage='To confirm deletion, enter "Delete" in the field.'
						title={`Delete ${team.name}`}
						onConfirm={handleDeleteTeam}
					>
						<Button className="w-max" accentColor="danger" size="sm">
							Delete
						</Button>
					</TextConfirmationDialog>
				</OrganizationAccessRequired>
			</section>
		</ViewContainer>
	)
})
TeamSettingsDetailView.displayName = "TeamSettingsDetailView"
